import { TokenStorage, UserStorage } from './cookieStorage';
import { message } from 'antd';
import { IDLE_TIMEOUT_ENABLED, IDLE_TIMEOUT_MINUTES } from '../constants/APP_CONSTANTS';
import axios from 'axios';

const BASE_API = window.location.origin + '/api';

// Session configuration - use a function to get dynamic config values
const getSessionConfig = () => {
  const idleTimeoutMinutes = window.ENV_CONFIG?.IDLE_TIMEOUT_MINUTES ?? IDLE_TIMEOUT_MINUTES;
  return {
    MAX_IDLE_TIME: idleTimeoutMinutes * 60 * 1000, // Configurable idle timeout
    WARNING_TIME: 5 * 60 * 1000,   // 5 minutes before expiry
    REFRESH_THRESHOLD: 5 * 60 * 1000, // Refresh token 5 minutes before expiry
    HEARTBEAT_INTERVAL: 60 * 1000,    // Check session every minute
  };
};

// Keep for backward compatibility but will use getSessionConfig() where needed
const SESSION_CONFIG = getSessionConfig();

class SessionManager {
  constructor() {
    this.lastActivity = Date.now();
    this.warningShown = false;
    this.heartbeatInterval = null;
    this.sessionTimeoutWarning = null;
    this.eventListeners = [];
    this.onSessionExpired = null;
    this.onSessionWarning = null;
    this.initialized = false;

    // Wait for ENV_CONFIG to be loaded before initializing
    if (typeof window !== 'undefined') {
      this.waitForConfigAndInitialize();
    }
  }

  waitForConfigAndInitialize() {
    // Check if ENV_CONFIG is already loaded
    if (window.ENV_CONFIG) {
      this.initializeSession();
      return;
    }

    // Poll for ENV_CONFIG with timeout
    const maxAttempts = 50; // 5 seconds max wait
    let attempts = 0;

    const checkConfig = () => {
      attempts++;
      if (window.ENV_CONFIG) {
        this.initializeSession();
      } else if (attempts < maxAttempts) {
        setTimeout(checkConfig, 100);
      } else {
        // Fallback to defaults after timeout
        console.warn('ENV_CONFIG not loaded after 5 seconds, using defaults');
        this.initializeSession();
      }
    };

    setTimeout(checkConfig, 100);
  }

  initializeSession() {
    // Prevent double initialization
    if (this.initialized) {
      return;
    }

    // Only initialize if idle timeout is enabled - check dynamically from ENV_CONFIG
    const isIdleTimeoutEnabled = window.ENV_CONFIG?.IDLE_TIMEOUT_ENABLED ?? IDLE_TIMEOUT_ENABLED;
    if (!isIdleTimeoutEnabled) {
      console.log('Idle timeout is disabled via ENV_CONFIG');
      this.initialized = true;
      return;
    }

    console.log('Initializing session manager with idle timeout enabled');
    this.initialized = true;

    // Track user activity
    this.trackUserActivity();

    // Start heartbeat monitoring
    this.startHeartbeat();

    // Handle browser close/refresh
    this.handlePageUnload();
  }

  trackUserActivity() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const updateActivity = () => {
      this.lastActivity = Date.now();
      this.warningShown = false;

      // Clear any existing timeout warning
      if (this.sessionTimeoutWarning) {
        clearTimeout(this.sessionTimeoutWarning);
        this.sessionTimeoutWarning = null;
      }
    };

    events.forEach(event => {
      const listener = updateActivity;
      document.addEventListener(event, listener, true);
      this.eventListeners.push({ event, listener });
    });
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.checkSession();
    }, SESSION_CONFIG.HEARTBEAT_INTERVAL);
  }

  isUnprotectedRoute() {
    const currentPath = window.location.pathname;

    // Check if route has no-auth prefix
    if (currentPath.startsWith('/no-auth/')) {
      return true;
    }

    // Legacy unprotected routes
    const unprotectedRoutes = [
      '/login',
      '/study-share/'
    ];

    return unprotectedRoutes.some(route => currentPath.includes(route)) ||
           new URLSearchParams(window.location.search).get('shared') === 'true';
  }

  checkSession() {
    // Skip if idle timeout is disabled - check dynamically from ENV_CONFIG
    const isIdleTimeoutEnabled = window.ENV_CONFIG?.IDLE_TIMEOUT_ENABLED ?? IDLE_TIMEOUT_ENABLED;
    if (!isIdleTimeoutEnabled) {
      return;
    }

    // Skip session checks for unprotected routes
    if (this.isUnprotectedRoute()) {
      return;
    }

    const now = Date.now();
    const timeSinceActivity = now - this.lastActivity;
    const tokenExpiration = TokenStorage.getTokenExpiration();

    // Get dynamic session config
    const config = getSessionConfig();

    // Check if session is idle
    if (timeSinceActivity > config.MAX_IDLE_TIME) {
      this.expireSession('idle_timeout');
      return;
    }

    // Check if token is expired
    if (!TokenStorage.isTokenValid()) {
      this.expireSession('token_expired');
      return;
    }

    // Show warning if approaching idle timeout
    const timeUntilIdle = config.MAX_IDLE_TIME - timeSinceActivity;
    if (timeUntilIdle <= config.WARNING_TIME && !this.warningShown) {
      this.showSessionWarning(Math.ceil(timeUntilIdle / 1000 / 60));
    }

    // Check if token needs refresh
    if (tokenExpiration && (tokenExpiration - now) <= config.REFRESH_THRESHOLD) {
      this.refreshToken();
    }
  }

  showSessionWarning(minutesLeft) {
    this.warningShown = true;

    if (this.onSessionWarning) {
      this.onSessionWarning(minutesLeft);
    } else {
      message.warning({
        content: `Your session will expire in ${minutesLeft} minutes. Click anywhere to extend your session.`,
        duration: 10,
        key: 'session-warning'
      });
    }

    // Auto-expire session after warning time
    const config = getSessionConfig();
    this.sessionTimeoutWarning = setTimeout(() => {
      this.expireSession('idle_timeout_after_warning');
    }, config.WARNING_TIME);
  }

  async refreshToken() {
    try {
      const refreshToken = TokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Make API call to refresh token
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Store new tokens
      TokenStorage.setAccessToken(data.access_token, data.expires_in);
      if (data.refresh_token) {
        TokenStorage.setRefreshToken(data.refresh_token);
      }

    } catch (error) {
      console.error('Token refresh failed:', error);
      this.expireSession('refresh_failed');
    }
  }

  async expireSession(reason = 'unknown') {
    // Skip expiration for unprotected routes
    if (this.isUnprotectedRoute()) {
      return;
    }

    // Call backend logout endpoint to invalidate session in database
    try {
      const token = TokenStorage.getAccessToken();
      if (token) {
        await axios.post(BASE_API + '/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      // Even if API call fails, proceed with local cleanup
      console.error('Session expiry logout API call failed:', error);
    }

    // Clear all stored data
    this.clearSession();

    // Notify listeners
    if (this.onSessionExpired) {
      this.onSessionExpired(reason);
    } else {
      message.error('Your session has expired. Please log in again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    }
  }

  clearSession() {
    // Clear tokens and user data
    TokenStorage.clearAllTokens();
    UserStorage.removeUserDetails();

    // Clear intervals and timeouts
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.sessionTimeoutWarning) {
      clearTimeout(this.sessionTimeoutWarning);
      this.sessionTimeoutWarning = null;
    }

    // Remove event listeners
    this.eventListeners.forEach(({ event, listener }) => {
      document.removeEventListener(event, listener, true);
    });
    this.eventListeners = [];
  }

  handlePageUnload() {
    window.addEventListener('beforeunload', () => {
      // Save current session state
      sessionStorage.setItem('last_activity', this.lastActivity.toString());
    });

    // Check if returning from a page reload
    window.addEventListener('load', () => {
      const lastActivity = sessionStorage.getItem('last_activity');
      if (lastActivity) {
        const config = getSessionConfig();
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
        if (timeSinceLastActivity > config.MAX_IDLE_TIME) {
          this.expireSession('page_reload_timeout');
        }
      }
    });
  }

  // Public methods for session control
  extendSession() {
    this.lastActivity = Date.now();
    this.warningShown = false;
    if (this.sessionTimeoutWarning) {
      clearTimeout(this.sessionTimeoutWarning);
      this.sessionTimeoutWarning = null;
    }
  }

  async logout() {
    await this.expireSession('manual_logout');
  }

  isSessionActive() {
    // If idle timeout is disabled, session is always active based on token validity only
    const isIdleTimeoutEnabled = window.ENV_CONFIG?.IDLE_TIMEOUT_ENABLED ?? IDLE_TIMEOUT_ENABLED;
    if (!isIdleTimeoutEnabled) {
      return TokenStorage.isTokenValid();
    }

    const config = getSessionConfig();
    const timeSinceActivity = Date.now() - this.lastActivity;
    return timeSinceActivity <= config.MAX_IDLE_TIME && TokenStorage.isTokenValid();
  }

  getSessionInfo() {
    const config = getSessionConfig();
    const tokenExpiration = TokenStorage.getTokenExpiration();
    const timeSinceActivity = Date.now() - this.lastActivity;

    return {
      isActive: this.isSessionActive(),
      lastActivity: this.lastActivity,
      timeSinceActivity,
      tokenExpiration,
      timeUntilTokenExpiry: tokenExpiration ? tokenExpiration - Date.now() : null,
      timeUntilIdleTimeout: config.MAX_IDLE_TIME - timeSinceActivity,
    };
  }

  // Event handlers
  setSessionExpiredHandler(handler) {
    this.onSessionExpired = handler;
  }

  setSessionWarningHandler(handler) {
    this.onSessionWarning = handler;
  }

  // Static method to get singleton instance
  static getInstance() {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();

// React hook for session management
export const useSessionManager = () => {
  const [sessionInfo, setSessionInfo] = React.useState(sessionManager.getSessionInfo());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setSessionInfo(sessionManager.getSessionInfo());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    sessionInfo,
    extendSession: () => sessionManager.extendSession(),
    logout: () => sessionManager.logout(),
    isActive: sessionManager.isSessionActive(),
  };
};

export default sessionManager;